// app/hooks/useVehicles.ts
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDriverVehicles, addVehicle, getMyVehicles } from '../api/apiService';
import { Vehicle, VehicleFormData } from '../lib/types';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import AsyncStorage from '@react-native-async-storage/async-storage';

const LAST_SELECTED_VEHICLE_KEY = '@last_selected_vehicle';

interface UseVehiclesProps {
  hasActiveEmployment: boolean | null;
  /**
   * WS7 (#207): a driver funding their own license operates without waiting
   * for the employer to activate the employment — their vehicles must load
   * just the same.
   */
  hasSelfPayLicense?: boolean;
}

export const useVehicles = ({ hasActiveEmployment, hasSelfPayLicense = false }: UseVehiclesProps) => {
  const { t } = useTranslation();
  const [hasVehicles, setHasVehicles] = useState<boolean | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<number | null>(null);
  const [vehicleModalVisible, setVehicleModalVisible] = useState(false);
  const [localRegNumber, setLocalRegNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const FETCH_COOLDOWN = 5000; // 5 seconds cooldown between fetches

  const fetchVehicleData = async (force = false): Promise<Vehicle[]> => {
    const now = Date.now();
    if (isLoading || (!force && now - lastFetchTime < FETCH_COOLDOWN)) return vehicles;
    
    setIsLoading(true);
    try {
      // Use getMyVehicles for driver self-management
      const response = await getMyVehicles();
      if (response.ok) {
        const fetchedVehicles = response.data.vehicles || [];
        const hasVehiclesData = response.data.has_vehicles;
        setHasVehicles(hasVehiclesData);
        setVehicles(fetchedVehicles);
        
        // Try to restore last selected vehicle from storage
        if (hasVehiclesData && fetchedVehicles.length > 0) {
          if (!selectedVehicle) {
            try {
              const lastSelected = await AsyncStorage.getItem(LAST_SELECTED_VEHICLE_KEY);
              if (lastSelected) {
                const lastSelectedId = parseInt(lastSelected);
                // Check if last selected vehicle still exists
                const vehicleStillExists = fetchedVehicles.some(v => v.id === lastSelectedId);
                if (vehicleStillExists) {
                  setSelectedVehicle(lastSelectedId);
                } else {
                  // Fall back to first vehicle if last selected is gone
                  setSelectedVehicle(fetchedVehicles[0].id);
                  await AsyncStorage.setItem(LAST_SELECTED_VEHICLE_KEY, fetchedVehicles[0].id.toString());
                }
              } else {
                // First time, use first vehicle
                setSelectedVehicle(fetchedVehicles[0].id);
                await AsyncStorage.setItem(LAST_SELECTED_VEHICLE_KEY, fetchedVehicles[0].id.toString());
              }
            } catch (e) {
              console.error('Error loading last selected vehicle:', e);
              setSelectedVehicle(fetchedVehicles[0].id);
            }
          }
        }
        setLastFetchTime(now);
        return fetchedVehicles;
      }
      return vehicles;
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      return vehicles;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Save selected vehicle to storage when it changes
  const handleSetSelectedVehicle = async (vehicleId: number | null) => {
    setSelectedVehicle(vehicleId);
    if (vehicleId !== null) {
      try {
        await AsyncStorage.setItem(LAST_SELECTED_VEHICLE_KEY, vehicleId.toString());
      } catch (e) {
        console.error('Error saving selected vehicle:', e);
      }
    }
  };

  // Initial fetch when employment status changes
  useEffect(() => {
    if (hasActiveEmployment === true || hasSelfPayLicense) {
      fetchVehicleData(true);
    }
  }, [hasActiveEmployment, hasSelfPayLicense]);

  const handleAddVehicle = async (data: VehicleFormData, form?: UseFormReturn<VehicleFormData>) => {
    try {
      const response = await addVehicle(data.registration_number, data.fuel_type, data.make, data.model, data.year);
      if (response.ok || response.status === 201) {
        setVehicleModalVisible(false);
        setLocalRegNumber(''); // Reset local registration number
        await fetchVehicleData();
        return true; // Success
      } else {
        // Map server errors to form fields if form is provided
        if (form && response.data) {
          const errorData: any = response.data;
          let hasFieldError = false;
          ['registration_number', 'fuel_type', 'make', 'model', 'year'].forEach((field) => {
            if (errorData[field]) {
              hasFieldError = true;
              let message = '';
              if (Array.isArray(errorData[field])) {
                message = errorData[field][0];
              } else if (typeof errorData[field] === 'string') {
                try {
                  let parsed: any = errorData[field];
                  if (typeof parsed === 'string') {
                    parsed = parsed.replace(/'/g, '"');
                    parsed = JSON.parse(parsed);
                  }
                  if (Array.isArray(parsed)) {
                    message = parsed[0];
                  } else {
                    message = errorData[field];
                  }
                } catch {
                  message = errorData[field];
                }
              }
              form.setError(field as any, { type: 'manual', message });
            }
          });
          if (hasFieldError) return false;
        }
        // fallback
        alert(`${t('errorAddingVehicle')}: ${JSON.stringify(response.data)}`);
        return false;
      }
    } catch (error) {
      console.error('Error adding vehicle:', error);
      if (form) {
        form.setError('registration_number', { type: 'manual', message: t('errorAddingVehicle') });
      } else {
        alert(t('errorAddingVehicle'));
      }
      return false;
    }
  };

  const formatRegistrationNumber = (input: string): string => {
    const cleanInput = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const chars = cleanInput.slice(0, 6);
    if (chars.length <= 2) return chars;
    if (chars.length <= 4) return `${chars.slice(0, 2)}-${chars.slice(2)}`;
    return `${chars.slice(0, 2)}-${chars.slice(2, 4)}-${chars.slice(4)}`;
  };

  useEffect(() => {
    if (!vehicleModalVisible) setLocalRegNumber('');
  }, [vehicleModalVisible]);

  return {
    hasVehicles,
    vehicles,
    selectedVehicle,
    setSelectedVehicle: handleSetSelectedVehicle,
    vehicleModalVisible,
    setVehicleModalVisible,
    localRegNumber,
    setLocalRegNumber,
    fetchVehicleData,
    handleAddVehicle,
    formatRegistrationNumber,
    isLoading,
  };
};